import React from 'react';
import { I18n } from 'react-redux-i18n';
import { RefreshCw01 } from '@untitledui/icons';

import { I18N_BASE } from '../../constants';
import CmButton from '../../components/primitives/CmButton';
import styles from './queue.module.css';

export interface RefreshIndicatorProps {
  lastFetchedAt: number | null;
  secondsUntilRefresh: number;
  isPolling: boolean;
  onRefresh: () => void;
  testId?: string;
}

function formatCountdown(seconds: number): string {
  const safe = Math.max(0, seconds);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatLastUpdated(lastFetchedAt: number | null): string {
  if (lastFetchedAt == null) return I18n.t(`${I18N_BASE}.queue.refresh.never`);
  const ageSeconds = Math.floor((Date.now() - lastFetchedAt) / 1000);
  if (ageSeconds < 5) return I18n.t(`${I18N_BASE}.queue.refresh.justNow`);
  if (ageSeconds < 60) return I18n.t(`${I18N_BASE}.queue.refresh.secondsAgo`, { seconds: ageSeconds });
  const minutes = Math.floor(ageSeconds / 60);
  return I18n.t(`${I18N_BASE}.queue.refresh.minutesAgo`, { minutes });
}

export function RefreshIndicator({
  lastFetchedAt,
  secondsUntilRefresh,
  isPolling,
  onRefresh,
  testId,
}: RefreshIndicatorProps): React.ReactElement {
  return (
    <div className={styles.refreshIndicator} data-testid={testId}>
      <span className={styles.lastUpdated}>
        {I18n.t(`${I18N_BASE}.queue.refresh.lastUpdated`)} {formatLastUpdated(lastFetchedAt)}
      </span>
      <CmButton
        variant="ghost"
        size="tiny"
        className={`${styles.refreshButton} ${isPolling ? styles.refreshSpinning : ''}`}
        onClick={onRefresh}
        disabled={isPolling}
        title={I18n.t(`${I18N_BASE}.queue.refresh.buttonTitle`, {
          countdown: formatCountdown(secondsUntilRefresh),
        })}
        testId={testId ? `${testId}-button` : undefined}
      >
        <RefreshCw01 width={14} height={14} />
        <span className={styles.refreshCountdown}>
          {formatCountdown(secondsUntilRefresh)}
        </span>
      </CmButton>
    </div>
  );
}

export default RefreshIndicator;
